import '@/configs/firebase-auth';
import { until } from '@vueuse/core';
import {
  FacebookAuthProvider,
  getAuth,
  GoogleAuthProvider,
  isSignInWithEmailLink,
  sendSignInLinkToEmail,
  signInWithEmailAndPassword,
  signInWithEmailLink,
  signInWithPopup,
  updatePassword,
  type User,
} from 'firebase/auth';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import type { UserId } from '@scaffold/common/user';

import { busyAction, untilAction } from '@/core/attachable-store';
import { PermissionDeniedError, UnauthenticatedError } from '@/core/errors';

const AUTH_EMAIL_KEY = 'authEmail';

export function getRecentEmailLink() {
  return import.meta.env.SSR
    ? undefined
    : window.localStorage.getItem(AUTH_EMAIL_KEY);
}

export const useAuthStore = defineStore('auth', () => {
  const loading = ref(true);
  const uid = ref<UserId | null>();
  const email = ref('');
  const name = ref('');
  const isAdmin = ref(false);
  const providerIds = ref<string[]>([]);

  let user: User | null = null;

  async function applyUser(user: User | null, refresh: boolean = false) {
    if (!user) {
      uid.value = null;
      email.value = '';
      name.value = '';
      isAdmin.value = false;
      providerIds.value = [];

      return;
    }

    const { claims } = await user.getIdTokenResult(refresh);

    uid.value = user.uid as UserId;

    email.value = ((claims.email as string) || user.email) ?? '';
    name.value = ((claims.name as string) || user.displayName) ?? '';
    isAdmin.value = !!claims.admin;
    providerIds.value = user.providerData.map((data) => data.providerId);
  }

  const refreshClaims = untilAction(loading, async () => {
    if (user) await applyUser(user, true);
  });

  const signInGoogle = busyAction(loading, async () => {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({
      prompt: 'select_account',
    });

    const { user: newUser } = await signInWithPopup(getAuth(), provider);

    await applyUser(newUser);
    user = newUser;
  });

  const signInFacebook = busyAction(loading, async () => {
    const provider = new FacebookAuthProvider();
    // provider.setCustomParameters({
    //   display: 'popup',
    // });

    const { user: newUser } = await signInWithPopup(getAuth(), provider);

    await applyUser(newUser);
    user = newUser;
  });

  const sendEmailLink = busyAction(loading, async (email: string) => {
    await sendSignInLinkToEmail(getAuth(), email, {
      url: location.origin + '/auth?email',
      handleCodeInApp: true,
    });

    window.localStorage.setItem(AUTH_EMAIL_KEY, email);
  });

  const signInEmailLink = busyAction(
    loading,
    async (email: string, emailLink: string) => {
      const { user: newUser } = await signInWithEmailLink(
        getAuth(),
        email,
        emailLink,
      );
      window.localStorage.removeItem(AUTH_EMAIL_KEY);

      await applyUser(newUser);
      user = newUser;
    },
  );

  const signInPassword = busyAction(
    loading,
    async (email: string, password: string) => {
      await signInWithEmailAndPassword(getAuth(), email, password);
    },
  );

  const signOut = busyAction(loading, async () => {
    await getAuth().signOut();
  });

  const unsubscribeFirstAuth = getAuth().onAuthStateChanged(async (newUser) => {
    await applyUser(newUser);
    user = newUser;

    unsubscribeFirstAuth();
    loading.value = false;

    getAuth().onAuthStateChanged(
      untilAction(loading, async (newUser) => {
        if (user !== newUser) {
          await applyUser(newUser);
          user = newUser;
        }
      }),
    );
  });

  async function untilReady() {
    await until(loading).toBe(false);
  }

  return {
    loading,
    uid,
    email,
    name,
    isAdmin,
    providerIds,
    untilReady,
    signInGoogle,
    signInFacebook,
    sendEmailLink,
    isEmailLink: (emailLink: string) =>
      isSignInWithEmailLink(getAuth(), emailLink),
    signInEmailLink,
    signInPassword,
    updatePassword: busyAction(loading, async (password: string) => {
      if (!user) throw new Error('No user');

      await updatePassword(user, password);
    }),
    hasPassword: computed(() => providerIds.value.includes('password')),
    signOut,
    refreshClaims,
  };
});

export const checkAuthStore = async (admin?: boolean) => {
  const authStore = useAuthStore();
  await authStore.untilReady();

  if (!authStore.uid) throw new UnauthenticatedError();
  if (admin && !authStore.isAdmin) throw new PermissionDeniedError();

  return authStore;
};
